import React, { useEffect, useMemo, useState } from "react";
import { useContextStateManagement } from "../../context/ContextApi";
import DateRangePicker from "../Home/DateRangePicker";
import "../Dashboard/Dashboard.css";
import "./AdminDashboard.css";

export const AdminDashboard = () => {
  const { token, userType, usersPostRequest, setSideBarLinkActive } =
    useContextStateManagement();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (setSideBarLinkActive) {
      setSideBarLinkActive("adminDashboard");
    }
  }, [setSideBarLinkActive]);

  useEffect(() => {
    let isMounted = true;

    const loadStats = async () => {
      if (!token) return;

      if (userType !== "admin") {
        if (isMounted) {
          setError("Not authorized.");
          setRows([]);
        }
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = {
          memberListName: "LeadGenerationUploadLead",
          startDate,
          endDate,
        };
        const data = await usersPostRequest(
          token,
          "/api/admin/practiceCallStats",
          payload,
        );
        if (!isMounted) return;

        if (data === "Token is expired, login again") {
          setError("Token is expired, login again");
          setRows([]);
        } else if (Array.isArray(data)) {
          setRows(data);
        } else {
          setRows([]);
          setError(data?.error || "Failed to load admin dashboard data");
        }
      } catch (err) {
        if (isMounted) {
          setError("Failed to load admin dashboard data");
          setRows([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadStats();

    return () => {
      isMounted = false;
    };
  }, [token, userType, usersPostRequest, startDate, endDate]);

  const sortedRows = [...rows].sort(
    (a, b) => (Number(b.callCount) || 0) - (Number(a.callCount) || 0)
  );

  const practicesWithCalls = sortedRows.filter(
    (row) => Number(row.callCount) > 0 || Number(row.durationMinutes) > 0,
  ).length;
  const totalCalls = sortedRows.reduce(
    (sum, row) => sum + (Number(row.callCount) || 0),
    0,
  );
  const totalMinutes = sortedRows.reduce(
    (sum, row) => sum + (Number(row.durationMinutes) || 0),
    0,
  );
  const topPractice = sortedRows[0];
  const topPracticeName = topPractice?.practiceName || "No practice";
  const topPracticeCalls = Number(topPractice?.callCount) || 0;
  const topPracticeMinutes = Number(topPractice?.durationMinutes) || 0;

  const handleDateRangeChange = (nextStartDate, nextEndDate) => {
    setStartDate(nextStartDate || "");
    setEndDate(nextEndDate || "");
  };

  const tableRows = useMemo(
    () =>
      sortedRows.map((row) => ({
        key: row.practiceId || row.practiceName || `${row.inboundNumber}-${row.outboundNumber}`,
        practiceName: row.practiceName || "-",
        inboundNumber: row.inboundNumber || "-",
        outboundNumber: row.outboundNumber || "-",
        inboundCalls: row.inboundCalls ?? 0,
        outboundCalls: row.outboundCalls ?? 0,
        totalCalls: row.callCount ?? 0,
        totalMinutes: Number(row.durationMinutes ?? 0).toFixed(2),
      })),
    [sortedRows],
  );

  return (
    <div className="dashboard-wrap admin-insights-shell">
      <div className="admin-insights-topbar">
        <div className="admin-insights-topbar-spacer" aria-hidden="true" />
        <div className="admin-insights-topbar-range">
          <div className="admin-insights-range-control">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onRangeChange={handleDateRangeChange}
            />
          </div>
        </div>
      </div>

      {loading && <p className="text-secondary">Loading admin dashboard...</p>}
      {error && <p className="text-danger">{error}</p>}

      {!loading && !error && (
        <>
          <div className="admin-kpi-grid">
            <div className="admin-kpi-card admin-kpi-card--top-practice">
              <div className="admin-kpi-flip">
                <div className="admin-kpi-face admin-kpi-face--front">
                  <p className="admin-kpi-label">Top Practice</p>
                  <h3 className="admin-kpi-title">{topPracticeName}</h3>
                  <p className="admin-kpi-subtext">
                    Highest total call volume in the selected range.
                  </p>
                </div>
                <div className="admin-kpi-face admin-kpi-face--back">
                  <div className="admin-kpi-back-metric">
                    <span className="admin-kpi-back-label">Total Calls</span>
                    <span className="admin-kpi-back-value">{topPracticeCalls}</span>
                  </div>
                  <div className="admin-kpi-back-metric">
                    <span className="admin-kpi-back-label">Total Minutes</span>
                    <span className="admin-kpi-back-value">
                      {topPracticeMinutes.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="admin-kpi-card admin-kpi-card--metric">
              <p className="admin-kpi-label admin-kpi-label--inverse">
                Practice With Calls
              </p>
              <h3 className="admin-kpi-value">{practicesWithCalls}</h3>
            </div>
            <div className="admin-kpi-card admin-kpi-card--metric">
              <p className="admin-kpi-label admin-kpi-label--inverse">
                Total Calls
              </p>
              <h3 className="admin-kpi-value">{totalCalls}</h3>
            </div>
            <div className="admin-kpi-card admin-kpi-card--metric">
              <p className="admin-kpi-label admin-kpi-label--inverse">
                Total Minutes
              </p>
              <h3 className="admin-kpi-value">{totalMinutes.toFixed(2)}</h3>
            </div>
          </div>

          <div className="admin-insights-table-shell">
            <div className="admin-table-header">
              <span>Practice</span>
              <span>Inbound Number</span>
              <span>Outbound Number</span>
              <span>Inbound Calls</span>
              <span>Outbound Calls</span>
              <span>Total Calls</span>
              <span>Total Minutes</span>
            </div>

            <div className="admin-insights-table-body">
              {tableRows.length === 0 ? (
                <div className="admin-table-empty">No practice stats available.</div>
              ) : (
                tableRows.map((row) => (
                  <div className="admin-table-row" key={row.key}>
                    <span className="admin-table-cell admin-practice-name">
                      {row.practiceName}
                    </span>
                    <span className="admin-table-cell" title={row.inboundNumber}>
                      {row.inboundNumber}
                    </span>
                    <span className="admin-table-cell" title={row.outboundNumber}>
                      {row.outboundNumber}
                    </span>
                    <span className="admin-table-cell admin-table-cell--numeric">
                      {row.inboundCalls}
                    </span>
                    <span className="admin-table-cell admin-table-cell--numeric">
                      {row.outboundCalls}
                    </span>
                    <span className="admin-table-cell admin-table-cell--numeric">
                      {row.totalCalls}
                    </span>
                    <span className="admin-table-cell admin-table-cell--numeric">
                      {row.totalMinutes}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

